import { useQuery } from '@tanstack/react-query'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { Printer, ShieldAlert } from 'lucide-react'
import { useMemo, useState } from 'react'

import { downloadSavingsStatement, downloadSavingsTransactionReceipt } from '@/api/documents-api'
import { savingsStatementQueryOptions } from '@/api/savings-api'
import type { SavingsAccount, SavingsTransaction } from '@/api/savings-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { ViewDocumentButton } from '@/components/document-viewer'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDateTime, formatShortDate } from '@/lib/datetime'

import { isCreditTransaction, transactionTypeLabel } from './constants'
import { SAVINGS_PERMISSIONS, useSavingsPermissions } from './permissions'

/**
 * The receipt icon-button on one statement row. Opens the receipt in the viewer: the first request
 * files it, every later one is a reprint of the copy on file.
 */
function ReceiptButton({
  accountId,
  transactionId,
  receiptNumber,
}: {
  accountId: string
  transactionId: string
  receiptNumber: string
}) {
  return (
    <ViewDocumentButton
      variant="ghost"
      size="icon-sm"
      aria-label="Print receipt"
      title={`Receipt ${receiptNumber}`}
      load={() => downloadSavingsTransactionReceipt(accountId, transactionId)}
    >
      <Printer />
    </ViewDocumentButton>
  )
}

/**
 * The statement, as the passbook records it.
 *
 * Nothing here is sortable. The endpoint takes no ordering parameter, and a statement reordered in
 * the browser would put a running balance column next to rows it does not describe — which is the
 * one column the whole table exists for.
 *
 * On a phone the table stacks into cards, like every other table in this application. The argument
 * for making a statement the exception — that its meaning is in the running balance beside each row
 * — is real but does not survive the alternative: horizontal scrolling of a data table is not an
 * acceptable phone experience, and the policy says so without an exception. The card keeps the
 * balance, promoted to the line under the heading rather than buried among the labelled pairs, so
 * it is read with the row rather than scrolled to.
 */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const DEFAULT_PAGE_SIZE = 25

const helper = dataTableColumns<SavingsTransaction>()

/** Bound to one account so the receipt column knows which account each row's transaction belongs to. */
const buildColumns = (accountId: string): DataTableColumn<SavingsTransaction>[] =>
  helper.columns([
    helper.accessor('valueDate', {
      header: 'Value date',
      enableSorting: false,
      cell: (context) => (
        <div className="space-y-0.5">
          <span className="numeric tabular-nums">{formatShortDate(context.row.original.valueDate)}</span>
          {context.row.original.valueDate !== context.row.original.businessDate ? (
            // Backdating is legitimate and bounded, and the two dates disagreeing is a fact a
            // reconciler needs rather than a defect to hide.
            <p className="text-2xs text-muted">
              posted {formatShortDate(context.row.original.businessDate)}
            </p>
          ) : null}
        </div>
      ),
      meta: { noWrap: true, widthClass: 'w-36', card: 'lead' },
    }),
    helper.accessor('type', {
      header: 'Type',
      enableSorting: false,
      cell: (context) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <span>{transactionTypeLabel(context.row.original.type)}</span>
          {context.row.original.isReversed ? <Badge tone="danger">Reversed</Badge> : null}
        </div>
      ),
      meta: { card: 'title' },
    }),
    helper.accessor('transactionNumber', {
      header: 'Transaction',
      enableSorting: false,
      cell: (context) => <span className="font-mono text-xs">{context.row.original.transactionNumber}</span>,
      meta: { noWrap: true, widthClass: 'w-40' },
    }),
    helper.accessor('amountMinor', {
      header: 'Amount',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          // Signed by direction rather than by the stored sign: the server sends a magnitude, and a
          // column of unsigned figures makes a withdrawal look like a deposit.
          value={
            isCreditTransaction(context.row.original.type)
              ? context.row.original.amountMinor
              : -context.row.original.amountMinor
          }
          currency={context.row.original.currencyCode}
          signed
          colourNegative
        />
      ),
      meta: { align: 'right', widthClass: 'w-40' },
    }),
    helper.accessor('balanceAfterMinor', {
      header: 'Balance',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.balanceAfterMinor}
          currency={context.row.original.currencyCode}
          emphasis="strong"
        />
      ),
      meta: { align: 'right', widthClass: 'w-40', card: 'lead' },
    }),
    helper.accessor('narration', {
      header: 'Narration',
      enableSorting: false,
      cell: (context) =>
        context.row.original.narration === null ? (
          <span className="text-muted">—</span>
        ) : (
          context.row.original.narration
        ),
    }),
    helper.accessor('postedBy', {
      header: 'Posted by',
      enableSorting: false,
      cell: (context) => (
        <div className="space-y-0.5">
          <span>{context.row.original.postedBy}</span>
          <p className="numeric text-2xs text-muted tabular-nums">
            {formatDateTime(context.row.original.postedAtUtc)}
          </p>
        </div>
      ),
      meta: { noWrap: true },
    }),
    helper.accessor('journalEntryNumber', {
      header: 'Journal',
      enableSorting: false,
      cell: (context) =>
        context.row.original.journalEntryNumber === null ? (
          <span className="text-muted">—</span>
        ) : (
          <span className="font-mono text-xs">{context.row.original.journalEntryNumber}</span>
        ),
      meta: { noWrap: true, widthClass: 'w-36' },
    }),
    helper.display({
      id: 'receipt',
      header: '',
      cell: (context) => (
        <ReceiptButton
          accountId={accountId}
          transactionId={context.row.original.id}
          receiptNumber={context.row.original.transactionNumber}
        />
      ),
      meta: { noWrap: true, widthClass: 'w-10' },
    }),
  ])

export function StatementPanel({ account }: { account: SavingsAccount }) {
  const permissions = useSavingsPermissions()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [applied, setApplied] = useState<{ from?: string; to?: string }>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const columns = useMemo(() => buildColumns(account.id), [account.id])

  const query = useQuery({
    ...savingsStatementQueryOptions(account.id, {
      from: applied.from,
      to: applied.to,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    enabled: permissions.canViewStatement,
  })

  if (!permissions.canViewStatement) {
    return (
      <Alert tone="warning">
        <ShieldAlert aria-hidden />
        <div className="space-y-1">
          <AlertTitle>The statement is restricted</AlertTitle>
          <AlertDescription>
            Reading an account&apos;s movements needs{' '}
            <span className="font-mono text-xs">{SAVINGS_PERMISSIONS.statementView}</span>, which is a
            separate permission from seeing the account itself. The balances above are what this role
            is served.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  const hasRange = applied.from !== undefined || applied.to !== undefined

  const apply = () => {
    setApplied({ from: from === '' ? undefined : from, to: to === '' ? undefined : to })
    setPagination((current) => ({ ...current, pageIndex: 0 }))
  }

  const clear = () => {
    setFrom('')
    setTo('')
    setApplied({})
    setPagination((current) => ({ ...current, pageIndex: 0 }))
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-card border border-border-subtle bg-surface p-3">
        <div className="min-w-40 flex-1 space-y-1.5">
          <Label htmlFor="statement-from">From</Label>
          <Input
            id="statement-from"
            type="date"
            value={from}
            max={to === '' ? undefined : to}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="min-w-40 flex-1 space-y-1.5">
          <Label htmlFor="statement-to">To</Label>
          <Input
            id="statement-to"
            type="date"
            value={to}
            min={from === '' ? undefined : from}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" onClick={apply}>
            Apply
          </Button>
          {hasRange ? (
            <Button variant="ghost" size="sm" onClick={clear}>
              Whole history
            </Button>
          ) : null}
          <ViewDocumentButton
            variant="secondary"
            size="sm"
            title={`Savings statement ${account.accountNumber}`}
            load={() => downloadSavingsStatement(account.id, { from: applied.from, to: applied.to })}
          >
            <Printer />
            Print statement
          </ViewDocumentButton>
        </div>
      </div>

      <p className="text-xs text-muted">
        A withdrawal against a product that charges a fee produces <strong>two</strong> lines: the
        withdrawal and a separate withdrawal fee, each with its own transaction number. Both belong
        in the paper passbook.
      </p>

      <DataTable
        columns={columns}
        data={query.data?.items}
        rowCount={query.data?.totalCount}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.id}
        emptyTitle={hasRange ? 'No movements in that period' : 'No movements yet'}
        emptyDescription={
          hasRange
            ? 'Nothing posted between those dates. Widen the range, or clear it to read the whole history.'
            : 'This account has never moved. Its opening deposit will be the first line.'
        }
        emptyAction={
          hasRange ? (
            <Button variant="secondary" size="sm" onClick={clear}>
              Read the whole history
            </Button>
          ) : null
        }
      />
    </div>
  )
}
